import {
  countedReps,
  format,
  formatSet,
  hasSides,
  isMyoRep,
  sideOf,
  workingSets,
  type Entry,
  type SetFigures,
  type Side,
} from "./session.ts";

/**
 * A finished session read against the time before, movement by movement: what
 * gets shown to the coach after a session done alone. The server already
 * sends, for each line, the previous time this movement was trained before
 * that session's date — not the latest, so an old session compares right.
 */

export type Trend = "up" | "down" | "same" | "first";

export type Lift = SetFigures;

export interface Gap {
  /** Heaviest load now minus heaviest load then. */
  loadDelta: number;
  /** Total reps now minus total reps then; null when the sheet kept no reps. */
  repsDelta: number | null;
  /** Load × reps now against then, as a fraction (0.26 is 26 % more); null without reps or load. */
  volumeChange: number | null;
  trend: Exclude<Trend, "first">;
}

/**
 * One line of the side-by-side: set N today next to set N the time before. A
 * myo-rep reads as two lines, its activation and its minis, since a mini
 * matched to a mini says nothing.
 */
export interface CompareRow {
  label: string;
  /** "36 × 8", "15,8 → 11,5 × 9", "6,8 × 5+5+5 (15)"; null when not done. */
  today: CompareCell | null;
  before: CompareCell | null;
  /** How today's line fares against the same line then; null when one is missing. */
  trend: Gap["trend"] | null;
}

/**
 * A set as one line, or a line a side when it was done one side at a time.
 * Written on one line it came out three ways down the same column — "15/14",
 * "G … · D …", "9/7 → 7/5" — and none could be read against the next (12/10).
 */
export type CompareCell = string | Record<Side, string>;

export interface Comparison {
  rows: CompareRow[];
  beforeDate: string | null;
  /** Null on a first time. */
  gap: Gap | null;
  trend: Trend;
}

/**
 * The heaviest load decides. At the same top load, the weight lifted over all
 * sets: counting reps alone, 6×10 6×12 6×12 came out behind 4×10 4×14 6×11
 * — a rep fewer, but two sets moved up to the top load. Reps settle what
 * the volume cannot weigh: nothing loaded, or the very same tonnage.
 */
export function gapBetween(now: Lift[], then: Lift[]): Gap {
  const loadDelta = round(top(now) - top(then));
  const withReps = [...now, ...then].every((set) => set.reps !== null);
  const repsDelta = withReps ? totalReps(now) - totalReps(then) : null;
  const before = withReps ? volume(then) : 0;
  const volumeChange = before > 0 ? volume(now) / before - 1 : null;
  const decider =
    loadDelta !== 0
      ? loadDelta
      : volumeChange !== null && round(volumeChange * 100) !== 0
        ? volumeChange
        : (repsDelta ?? 0);
  const trend = decider > 0 ? "up" : decider < 0 ? "down" : "same";
  return { loadDelta, repsDelta, volumeChange, trend };
}

/**
 * Null when nothing was logged in the app today — a checked-off line has
 * nothing to put side by side, and a sheet-only session has no reps — and on
 * the abs circuit.
 */
export function compareToPrevious(entry: Entry): Comparison | null {
  // A circuit's rounds are ticked, not lifted: holding a plank as long as
  // last week says nothing worth counting.
  if (entry.circuitGroup !== null) return null;
  const done: Lift[] = workingSets(entry);
  if (done.length === 0) return null;

  const previous: Lift[] = entry.previous?.sets ?? [];
  const rows = isMyoRep(entry.method) ? myoRows(done, previous) : setRows(done, previous);
  if (previous.length === 0) return { rows, beforeDate: null, gap: null, trend: "first" };

  const gap = gapBetween(done, previous);
  return { rows, beforeDate: entry.previous!.date, gap, trend: gap.trend };
}

/**
 * "+2,5 kg · +3 reps", "même charge · −1 rep", "=": the gap in a few
 * characters. When the reps point the other way from the verdict, the volume
 * that made it is what gets said — a green "−1 rep" reads as a mistake.
 */
export function formatGap(gap: Gap): string {
  const { loadDelta, repsDelta, volumeChange, trend } = gap;
  const reps =
    repsDelta !== null && repsDelta !== 0 ? `${signed(repsDelta)} rep${Math.abs(repsDelta) > 1 ? "s" : ""}` : null;
  if (loadDelta === 0) {
    const repsAgree = Math.sign(repsDelta ?? 0) === (trend === "up" ? 1 : trend === "down" ? -1 : 0);
    if (!repsAgree && volumeChange !== null) {
      return `même charge max · ${signed(Math.round(volumeChange * 100))} % de volume`;
    }
    return reps ? `même charge · ${reps}` : "=";
  }
  return reps ? `${signed(loadDelta)} kg · ${reps}` : `${signed(loadDelta)} kg`;
}

/**
 * Movements up, level and down against the time before. Spelled out rather
 * than "4/6": next to the count of movements done, a fraction reads as
 * "4 done out of 6".
 */
export function progressCount(comparisons: (Comparison | null)[]): { up: number; same: number; down: number } {
  const count = (trend: Trend) => comparisons.filter((c) => c?.trend === trend).length;
  return { up: count("up"), same: count("same"), down: count("down") };
}

/** "36 × 8", "8 × 9/8", "15,8 × 9 → 11,5 × 6", "34" from the sheet. Kilos go in the header. */
export function formatLift(set: Lift): string {
  return formatSet(set);
}

/**
 * Minis at one load share it: "6,8 × 5+5+5 (15)", "6,8 × 5+5 · 9 × 5 (15)".
 * The sheet's loads alone: "6,8".
 */
export function formatCluster(sets: Lift[]): string {
  if (sets.length === 1) return formatLift(sets[0]!);
  const groups: { load: Lift; reps: (number | null)[] }[] = [];
  for (const set of sets) {
    const last = groups.at(-1);
    if (last && last.load.weightKg === set.weightKg && last.load.dropWeightKg === set.dropWeightKg) {
      last.reps.push(set.reps);
    } else {
      groups.push({ load: set, reps: [set.reps] });
    }
  }
  if (sets.some((set) => set.reps === null)) {
    return groups
      .map((group) => formatLift({ weightKg: group.load.weightKg, dropWeightKg: group.load.dropWeightKg, reps: null }))
      .join(" · ");
  }
  const text = groups.map(
    (group) =>
      `${formatLift({ weightKg: group.load.weightKg, dropWeightKg: group.load.dropWeightKg, reps: null })} × ${group.reps.join("+")}`,
  );
  return `${text.join(" · ")} (${totalReps(sets)})`;
}

function setRows(done: Lift[], previous: Lift[]): CompareRow[] {
  return Array.from({ length: Math.max(done.length, previous.length) }, (_, index) => {
    const now = done[index];
    const then = previous[index];
    return {
      label: `S${index + 1}`,
      today: now ? cell(now) : null,
      before: then ? cell(then) : null,
      trend: now && then ? gapBetween([now], [then]).trend : null,
    };
  });
}

function cell(set: Lift): CompareCell {
  if (!hasSides(set)) return formatLift(set);
  return { left: formatLift(sideOf(set, "left")), right: formatLift(sideOf(set, "right")) };
}

function myoRows(done: Lift[], previous: Lift[]): CompareRow[] {
  const rows = [clusterRow("Activation", done.slice(0, 1), previous.slice(0, 1))];
  if (done.length > 1 || previous.length > 1) rows.push(clusterRow("Minis", done.slice(1), previous.slice(1)));
  return rows;
}

function clusterRow(label: string, now: Lift[], then: Lift[]): CompareRow {
  return {
    label,
    today: now.length > 0 ? formatCluster(now) : null,
    before: then.length > 0 ? formatCluster(then) : null,
    trend: now.length > 0 && then.length > 0 ? gapBetween(now, then).trend : null,
  };
}

function top(sets: Lift[]): number {
  return Math.max(...sets.map((set) => set.weightKg));
}

/** Load × reps over the sets, the drop of a drop set left out as everywhere else. */
function volume(sets: Lift[]): number {
  return sets.reduce((sum, set) => sum + set.weightKg * (countedReps(set) ?? 0), 0);
}

function totalReps(sets: Lift[]): number {
  return sets.reduce((sum, set) => sum + (countedReps(set) ?? 0), 0);
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}

function signed(value: number): string {
  return `${value > 0 ? "+" : "−"}${format(Math.abs(value))}`;
}
